import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { isMissing } from '../api/queries';
import type { CmdLogRow } from '../api/types';
import { useT } from '../i18n';
import { useStoredState } from '../lib/hooks';
import { fetchAppLog, maskSecrets, useCmdLog, type AppLog } from '../lib/robotLog';
import { timeStr } from '../lib/format';

type Tab = 'events' | 'cmd' | 'app';

/** Bottom-of-page logs: Events (live, this browser) | Command log (server) | Robot app log (from the robot). */
export function RobotLogs({ serial, events }: { serial: string; events: ReactNode }) {
  const t = useT();
  const [tab, setTab] = useStoredState<Tab>('cruzr.logsTab', 'events');
  return (
    <section className="logs-box" aria-label={t('clog.logs')}>
      <div role="tablist" className="seg sm logs-tabs">
        {(['events', 'cmd', 'app'] as Tab[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} aria-checked={tab === k} onClick={() => setTab(k)}>
            {t(`clog.tab.${k}`)}
          </button>
        ))}
      </div>
      {tab === 'events' && events}
      {tab === 'cmd' && <CommandLog serial={serial} />}
      {tab === 'app' && <AppLogView serial={serial} />}
    </section>
  );
}

const pretty = (v: unknown) => {
  try {
    return JSON.stringify(maskSecrets(v), null, 2);
  } catch {
    return String(v);
  }
};
const short = (v: unknown, max = 60) => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'string' ? v : pretty(v).replace(/\s+/g, ' ');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

function CommandLog({ serial }: { serial: string }) {
  const t = useT();
  const [kind, setKind] = useState<'' | 'cmd' | 'event'>('');
  const [name, setName] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(name), 300);
    return () => clearTimeout(id);
  }, [name]);
  const { rows, loading, error, missing, refetch } = useCmdLog(serial, kind, debounced);
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="clog">
      <div className="row clog-bar" style={{ gap: 8 }}>
        <div role="radiogroup" aria-label={t('clog.kind')} className="seg sm">
          {(['', 'cmd', 'event'] as const).map((k) => (
            <button key={k || 'all'} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}>
              {k === '' ? t('clog.all') : k === 'cmd' ? t('clog.cmds') : t('clog.events')}
            </button>
          ))}
        </div>
        <input className="input sm" type="search" placeholder={t('clog.nameFilter')} value={name} onChange={(e) => setName(e.target.value)} style={{ width: 160 }} aria-label={t('clog.nameFilter')} />
        <div className="grow" />
        <span className="muted" style={{ fontSize: 11 }}>{t('clog.rowsN', { n: rows.length })}</span>
        <button type="button" className="btn xs ghost" onClick={() => void refetch()} disabled={loading}>
          {t('clog.refresh')}
        </button>
      </div>
      <div className="clog-rows" role="list">
        <div className="clog-tr head" aria-hidden>
          <span>{t('clog.colTime')}</span>
          <span>{t('clog.colName')}</span>
          <span>{t('clog.colSource')}</span>
          <span>{t('clog.colResult')}</span>
          <span>{t('clog.colDuration')}</span>
          <span>{t('clog.colArgs')}</span>
        </div>
        {missing && <div className="note" style={{ margin: 8 }}>{t('clog.apiMissing')}</div>}
        {!missing && error ? <div className="note" style={{ margin: 8 }}>{String((error as Error).message)}</div> : null}
        {loading && !rows.length && <div className="muted" style={{ padding: 8 }}>{t('common.loading')}</div>}
        {!loading && !error && !rows.length && <div className="muted" style={{ padding: 8 }}>{t('clog.empty')}</div>}
        {rows.map((r) => (
          <CmdRow key={r.id} row={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />
        ))}
      </div>
    </div>
  );
}

function CmdRow({ row: r, open, onToggle }: { row: CmdLogRow; open: boolean; onToggle: () => void }) {
  const t = useT();
  const failed = r.ok === false || Boolean(r.errorCode);
  const hasDetails = (r.args && Object.keys(r.args).length > 0) || (r.data !== undefined && r.data !== null && !(typeof r.data === 'object' && !Object.keys(r.data as object).length)) || r.errorMessage;
  return (
    <div role="listitem" className={`clog-item${failed ? ' bad' : ''}`}>
      <button type="button" className={`clog-tr${open ? ' open' : ''}`} aria-expanded={open} onClick={onToggle} disabled={!hasDetails}>
        <span className="mono muted">{r.startedAt ? timeStr(r.startedAt) : '—'}</span>
        <span className="mono ellipsis" title={r.name}>
          <span className={`clog-kind ${r.kind}`}>{r.kind === 'event' ? '◆' : '→'}</span> {r.name}
        </span>
        <span className="ellipsis muted" title={r.source ?? ''}>
          {r.source ?? '—'}
        </span>
        <span className={`mono ${failed ? 'clog-err' : r.ok ? 'clog-ok' : 'muted'}`} title={r.errorMessage ?? undefined}>
          {failed ? (r.errorCode ?? t('clog.error')) : r.ok === true ? 'OK' : r.kind === 'event' ? '' : '…'}
        </span>
        <span className="mono muted">{typeof r.durationMs === 'number' ? `${r.durationMs} ms` : ''}</span>
        <span className="mono muted ellipsis">{hasDetails ? `${open ? '▾' : '▸'} ${short(r.args && Object.keys(r.args).length ? maskSecrets(r.args) : r.data)}` : ''}</span>
      </button>
      {open && (
        <div className="clog-details">
          {r.errorMessage && <div className="clog-err" style={{ marginBottom: 6 }}>{r.errorMessage}</div>}
          {r.args && Object.keys(r.args).length > 0 && (
            <>
              <span className="section-label">{t('clog.args')}</span>
              <pre className="mono">{pretty(r.args)}</pre>
            </>
          )}
          {r.data !== undefined && r.data !== null && (
            <>
              <span className="section-label">{t('clog.data')}</span>
              <pre className="mono">{pretty(r.data)}</pre>
            </>
          )}
          {r.requestId && <span className="muted mono" style={{ fontSize: 11 }}>{r.requestId}</span>}
        </div>
      )}
    </div>
  );
}

function AppLogView({ serial }: { serial: string }) {
  const t = useT();
  const [lines, setLines] = useStoredState<number>('cruzr.applogLines', 200);
  const [grep, setGrep] = useState('');
  const [log, setLog] = useState<AppLog | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const box = useRef<HTMLPreElement>(null);
  const load = async () => {
    setBusy(true);
    try {
      const r = await fetchAppLog(serial, lines, grep);
      setLog(r);
      setErr(null);
      setMissing(false);
      requestAnimationFrame(() => {
        if (box.current) box.current.scrollTop = box.current.scrollHeight;
      });
    } catch (e) {
      if (isMissing(e)) setMissing(true);
      else setErr(e instanceof ApiError ? e.message : String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="applog">
      <form
        className="row clog-bar"
        style={{ gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <input className="input sm mono" type="search" placeholder={t('clog.grep')} value={grep} onChange={(e) => setGrep(e.target.value)} style={{ width: 200 }} aria-label={t('clog.grep')} />
        <label className="row" style={{ gap: 6, fontSize: 12, color: 'var(--muted)' }}>
          {t('clog.lines')}
          <select className="select sm" value={lines} onChange={(e) => setLines(Number(e.target.value))} aria-label={t('clog.lines')}>
            {[100, 200, 500, 1000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="grow" />
        {log && <span className="muted mono" style={{ fontSize: 11 }}>{log.files.join(' · ')}</span>}
        <button type="submit" className="btn xs" disabled={busy}>
          {busy ? <span className="spinner sm" aria-hidden /> : log ? t('clog.refresh') : t('clog.load')}
        </button>
      </form>
      {missing && <div className="note" style={{ margin: 8 }}>{t('clog.apiMissing')}</div>}
      {err && <div className="note" style={{ margin: 8 }}>{err}</div>}
      <pre ref={box} className="applog-body mono" aria-live="polite">
        {log ? (log.lines.length ? log.lines.join('\n') : t('clog.noLines')) : !busy && !missing && !err ? t('clog.appHint') : ''}
      </pre>
    </div>
  );
}
